import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { useGetMediaView } from '@cobliteam/hooks/src/useCamera/useCameraMedia';
import { useTranslation } from '@cobliteam/i18n';
import Dialog from '@cobliteam/ui/components/Dialog';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import CobliCamPlayer from '@cobliteam/ui-business/components/Player/CobliCamPlayer';
import { useState } from 'react';
import RiskFootageThumbnail from './RiskFootageThumbnail';

interface RiskFootageDialogProps {
  /** Highlight aberto (traz `media_ids` + `signed_media_urls`). `null` = modal fechado. */
  highlight: RiskRecognitionHighlight | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal de footage do risco. Um highlight pode ter N clipes (`media_ids`); o seletor embaixo troca entre
 * eles e o vídeo selecionado é buscado em `/media` (getMediaView) e tocado pelo CobliCamPlayer. Os posters
 * do seletor saem de `signed_media_urls` (paralelo a `media_ids`), sem fetch extra. Montar com
 * `key={highlight?.id}` reseta a seleção pro 1º clipe ao abrir outro item.
 */
const RiskFootageDialog = ({ highlight, open, onOpenChange }: RiskFootageDialogProps) => {
  const { t } = useTranslation('notifications');
  const mediaIds = highlight?.metadata?.media_ids ?? [];
  const posters = highlight?.metadata?.signed_media_urls ?? [];
  const [selected, setSelected] = useState(0);

  const selectedMediaId = mediaIds[selected];
  const { data: mediaView, isError } = useGetMediaView(selectedMediaId, false, { enabled: open && Boolean(selectedMediaId) });
  const hasMultiple = mediaIds.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size='md'>
        <Dialog.Header>
          <Dialog.Title>{t('feed.footage.title')}</Dialog.Title>
          <Dialog.Description className='sr-only' />
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body className='space-y-3'>
          {isError ? (
            <div className='flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-lg bg-gray-2 text-center'>
              <Icon name='triangle-alert' className='text-danger' aria-hidden />
              <Typography variant='text-sm-medium'>{t('feed.footage.error')}</Typography>
            </div>
          ) : (
            <CobliCamPlayer className='aspect-video w-full' mediaView={mediaView} />
          )}

          {hasMultiple ? (
            <div className='flex gap-2 overflow-x-auto pb-1' role='tablist' aria-label={t('feed.footage.select')}>
              {mediaIds.map((mediaId, index) => (
                <button
                  key={mediaId}
                  type='button'
                  role='tab'
                  aria-selected={index === selected}
                  aria-label={t('feed.footage.clip', { index: index + 1 })}
                  onClick={() => setSelected(index)}
                  className={cn(
                    'relative h-14 w-20 shrink-0 overflow-hidden rounded-md border-2 transition-opacity',
                    index === selected ? 'border-primary-9' : 'border-transparent opacity-60 hover:opacity-100'
                  )}
                >
                  <RiskFootageThumbnail mediaId={mediaId} poster={posters[index]} enabled={open} />
                  <span className='absolute right-0.5 bottom-0.5 rounded bg-black/60 px-1 font-medium text-[10px] text-white leading-tight'>{index + 1}</span>
                </button>
              ))}
            </div>
          ) : null}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};

export default RiskFootageDialog;
